import { useState } from "react";
import JSZip from "jszip";

export default function FilesView({
  projects,
  companyContacts = [],
  setPreviewFile,
  canEdit,
  deleteProjectFileFromFilesView,
  setSelectedProject,
  setEditProject
}) {
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [selectedFileKeys, setSelectedFileKeys] = useState([]);
  const [isPreparingZip, setIsPreparingZip] = useState(false);

  const categories = [
    "Genel",
    "Faaliyet Raporu",
    "YMM",
    "Proje Belgesi",
    "Proje Bitirme Belgesi",
    "Yazılım Tescil",
    "Sözleşme",
    "Fatura",
    "Sunum",
    "Diğer"
  ];

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";

    const mb = bytes / (1024 * 1024);

    if (mb >= 1) {
      return `${mb.toFixed(2)} MB`;
    }

    const kb = bytes / 1024;
    return `${kb.toFixed(0)} KB`;
  };

  const getFileIcon = (fileName = "") => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (extension === "pdf") return "📕";
    if (["xls", "xlsx"].includes(extension)) return "📊";
    if (["doc", "docx"].includes(extension)) return "📄";
    if (["jpg", "jpeg", "png", "webp"].includes(extension)) return "🖼️";

    return "📎";
  };

  const normalizeCompanyName = (value = "") =>
    String(value || "")
      .trim()
      .toLocaleLowerCase("tr-TR")
      .replace(/[ıİ]/g, "i")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\b(a\.ş\.?|anonim|limited|ltd|şti|sirketi|şirketi|ticaret|sanayi|ve)\b/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const getFileKey = (file) => `${file.projectId || ""}-${file.path || file.url || file.name || ""}`;

  const sanitizeFileName = (value = "dosya") =>
    String(value || "dosya")
      .replace(/[\\/:*?"<>|]+/g, "-")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 120) || "dosya";

  const fetchFileBlobWithTimeout = async (file, timeoutMs = 20000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(file.url, {
        mode: "cors",
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`${file.name || "Dosya"} indirilemedi.`);
      }

      return await response.blob();
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error(`${file.name || "Dosya"} için indirme süresi doldu.`);
      }

      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const allFiles = projects.flatMap((project) =>
    (project.files || []).map((file) => ({
      ...file,
      projectId: project.id,
      firmaAdi: project.firmaAdi,
      projeAdi: project.projeAdi,
      teknoparkAdi: project.teknoparkAdi,
      projeStbKodu: project.projeStbKodu
    }))
  );

  const sortedFiles = [...allFiles].sort((a, b) => {
    return new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0);
  });

  const companyOptions = [
    "all",
    ...new Set(sortedFiles.map((file) => file.firmaAdi).filter(Boolean))
  ];

  const projectOptions = [
    "all",
    ...new Map(
      sortedFiles
        .filter((file) => companyFilter === "all" || file.firmaAdi === companyFilter)
        .map((file) => [
          file.projectId,
          {
            id: file.projectId,
            name: file.projeAdi || "Proje"
          }
        ])
    ).values()
  ].filter((item) => item === "all" || item.id);

  const filteredFiles = sortedFiles.filter((file) => {
    const search = searchText.toLowerCase().trim();

    const matchesSearch =
      !search ||
      file.name?.toLowerCase().includes(search) ||
      file.firmaAdi?.toLowerCase().includes(search) ||
      file.projeAdi?.toLowerCase().includes(search) ||
      file.teknoparkAdi?.toLowerCase().includes(search) ||
      file.projeStbKodu?.toString().toLowerCase().includes(search);

    const matchesCategory =
      categoryFilter === "all" || (file.category || "Genel") === categoryFilter;

    const matchesCompany =
      companyFilter === "all" || file.firmaAdi === companyFilter;

    const matchesProject =
      projectFilter === "all" || file.projectId === projectFilter;

    return matchesSearch && matchesCategory && matchesCompany && matchesProject;
  });

  const filteredFileKeys = filteredFiles.map(getFileKey);
  const selectedFiles = sortedFiles.filter((file) =>
    selectedFileKeys.includes(getFileKey(file))
  );
  const selectedFilteredCount = filteredFileKeys.filter((key) =>
    selectedFileKeys.includes(key)
  ).length;

  const getContactsForCompany = (companyName) => {
    const targetKey = normalizeCompanyName(companyName);

    if (!targetKey) return [];

    return (companyContacts || []).filter((contact) => {
      const contactCompanyKey = normalizeCompanyName(contact.companyName);
      const contactShortKey = normalizeCompanyName(contact.companyShortName);

      return (
        contact.email &&
        (
          contactCompanyKey === targetKey ||
          contactShortKey === targetKey ||
          (targetKey.length > 5 && contactCompanyKey.includes(targetKey)) ||
          (contactCompanyKey.length > 5 && targetKey.includes(contactCompanyKey))
        )
      );
    });
  };

  const openMailDraftForSelectedFiles = ({ zipFileName = "" } = {}) => {
    if (selectedFiles.length === 0) {
      alert("Mail hazırlamak için en az bir dosya seçiniz.");
      return;
    }

    const companies = [...new Set(selectedFiles.map((file) => file.firmaAdi).filter(Boolean))];
    const projectsForSubject = [...new Set(selectedFiles.map((file) => file.projeAdi).filter(Boolean))];
    const contactEmails = [
      ...new Set(
        companies.flatMap((companyName) =>
          getContactsForCompany(companyName).map((contact) => String(contact.email || "").trim())
        ).filter(Boolean)
      )
    ];

    const subjectCompany =
      companies.length === 1 ? companies[0] : `${companies.length} Firma`;
    const subjectProject =
      projectsForSubject.length === 1 ? ` - ${projectsForSubject[0]}` : "";
    const subject = `${subjectCompany}${subjectProject} - Proje Dosyaları`;

    const fileLines = selectedFiles.map((file, index) =>
      `${index + 1}. ${file.name || "Dosya"} | ${file.projeAdi || "-"} | ${file.category || "Genel"}`
    ).join("\n");

    const body = [
      "Merhaba,",
      "",
      zipFileName
        ? `İlgili dosyaları ${zipFileName} isimli ZIP dosyası olarak ekte iletiyoruz.`
        : "İlgili dosyaları ekte iletiyoruz.",
      "",
      "Dosya listesi:",
      fileLines,
      "",
      zipFileName
        ? "Not: ZIP dosyası bilgisayarınıza indirildi. Lütfen bu taslağa ek olarak iliştiriniz."
        : "",
      "İyi çalışmalar."
    ].filter(Boolean).join("\n");

    if (contactEmails.length === 0) {
      alert("Seçilen firma/firmalar için kayıtlı firma yetkilisi e-posta adresi bulunamadı. Outlook alıcı alanı boş açılacak.");
    }

    if (selectedFiles.length > 20) {
      alert("Çok sayıda dosya seçildiği için mail gövdesi uzun olabilir. Outlook açılmazsa daha az dosya seçerek deneyiniz.");
    }

    const recipients = contactEmails.map((email) => encodeURIComponent(email)).join(";");
    const mailto = `mailto:${recipients}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  const downloadSelectedFilesAsZip = async ({ openMailAfter = false } = {}) => {
    if (selectedFiles.length === 0) {
      alert("ZIP hazırlamak için en az bir dosya seçiniz.");
      return;
    }

    setIsPreparingZip(true);

    try {
      const companies = [...new Set(selectedFiles.map((file) => file.firmaAdi).filter(Boolean))];
      const projectsForName = [...new Set(selectedFiles.map((file) => file.projeAdi).filter(Boolean))];
      const baseZipName =
        companies.length === 1
          ? `${companies[0]}${projectsForName.length === 1 ? ` - ${projectsForName[0]}` : ""}`
          : "Secilen Proje Dosyalari";
      const zipFileName = `${sanitizeFileName(baseZipName)}.zip`;
      const zip = new JSZip();

      for (const [index, file] of selectedFiles.entries()) {
        if (!file.url) continue;

        const blob = await fetchFileBlobWithTimeout(file);
        const folderName = sanitizeFileName(file.projeAdi || file.firmaAdi || "Dosyalar");
        const fileName = sanitizeFileName(file.name || `dosya-${index + 1}`);

        zip.file(`${folderName}/${fileName}`, blob);
      }

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6
        }
      });
      const objectUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = zipFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);

      if (openMailAfter) {
        openMailDraftForSelectedFiles({ zipFileName });
      }
    } catch (error) {
      alert("ZIP hazırlanırken hata oluştu: " + error.message);
    } finally {
      setIsPreparingZip(false);
    }
  };

  const totalFilteredSize = filteredFiles.reduce(
    (total, file) => total + Number(file.size || 0),
    0
  );

  const categoryStats = categories.map((category) => ({
    name: category,
    count: allFiles.filter((file) => (file.category || "Genel") === category).length
  }));

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: 20 }}>Dosya Yönetimi</h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
          marginBottom: 20
        }}
      >
        <div style={summaryCardStyle}>
          <div style={summaryLabelStyle}>Toplam Dosya</div>
          <div style={summaryValueStyle}>{allFiles.length}</div>
        </div>

        <div style={summaryCardStyle}>
          <div style={summaryLabelStyle}>Filtrelenen Dosya</div>
          <div style={summaryValueStyle}>{filteredFiles.length}</div>
        </div>

        <div style={summaryCardStyle}>
          <div style={summaryLabelStyle}>Filtrelenen Boyut</div>
          <div style={summaryValueStyle}>{formatFileSize(totalFilteredSize)}</div>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          padding: 24,
          borderRadius: 18,
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)"
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 220px 260px 260px",
            gap: 12,
            marginBottom: 18
          }}
        >
          <input
            placeholder="Dosya, firma, proje, teknopark veya STB kodu ara..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={inputStyle}
          />

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="all">Tüm Kategoriler</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <select
            value={companyFilter}
            onChange={(e) => {
              setCompanyFilter(e.target.value);
              setProjectFilter("all");
            }}
            style={inputStyle}
          >
            <option value="all">Tüm Firmalar</option>
            {companyOptions
              .filter((company) => company !== "all")
              .map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
          </select>

          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            style={inputStyle}
          >
            <option value="all">Tüm Projeler</option>
            {projectOptions
              .filter((project) => project !== "all")
              .map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
          </select>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 22
          }}
        >
          <button
            onClick={() => {
              const nextKeys = [...new Set([...selectedFileKeys, ...filteredFileKeys])];
              setSelectedFileKeys(nextKeys);
            }}
            style={{
              ...categoryButtonStyle,
              background: "#e0f2fe",
              color: "#075985",
              border: "1px solid #bae6fd"
            }}
          >
            Filtrelenenleri Seç ({filteredFiles.length})
          </button>

          <button
            onClick={() => setSelectedFileKeys([])}
            style={{
              ...categoryButtonStyle,
              background: "#fff",
              color: "#64748b"
            }}
          >
            Seçimi Temizle
          </button>

          <button
            onClick={() => downloadSelectedFilesAsZip({ openMailAfter: true })}
            disabled={isPreparingZip}
            style={{
              ...categoryButtonStyle,
              background: selectedFiles.length > 0 ? "#124254" : "#f8fafc",
              color: selectedFiles.length > 0 ? "#fff" : "#64748b",
              border: selectedFiles.length > 0 ? "1px solid #124254" : "1px solid #e5e7eb",
              opacity: isPreparingZip ? 0.7 : 1,
              cursor: isPreparingZip ? "wait" : "pointer"
            }}
          >
            {isPreparingZip ? "ZIP Hazırlanıyor..." : `ZIP + Mail Hazırla (${selectedFiles.length})`}
          </button>

          <button
            onClick={() => downloadSelectedFilesAsZip()}
            disabled={isPreparingZip}
            style={{
              ...categoryButtonStyle,
              background: "#fff7ed",
              color: "#c2410c",
              border: "1px solid #fed7aa",
              opacity: isPreparingZip ? 0.7 : 1,
              cursor: isPreparingZip ? "wait" : "pointer"
            }}
          >
            ZIP İndir
          </button>

          <div style={{ width: "100%", height: 0 }} />

          <button
            onClick={() => setCategoryFilter("all")}
            style={{
              ...categoryButtonStyle,
              background: categoryFilter === "all" ? "#124254" : "#f8fafc",
              color: categoryFilter === "all" ? "#fff" : "#111827"
            }}
          >
            Tümü ({allFiles.length})
          </button>

          {categoryStats.map((category) => (
            <button
              key={category.name}
              onClick={() => setCategoryFilter(category.name)}
              style={getCategoryButtonStyle(category.name, categoryFilter === category.name)}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>

        {selectedFiles.length > 0 && (
          <div
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              borderRadius: 12,
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              color: "#075985",
              fontWeight: 800,
              fontSize: 13
            }}
          >
            {selectedFiles.length} dosya seçildi
            {selectedFilteredCount > 0 ? ` • Bu ekranda görünenlerden ${selectedFilteredCount} seçili` : ""}
          </div>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          {filteredFiles.map((file, index) => (
            <div
              key={`${file.projectId}-${file.path || index}`}
              style={fileCardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 14px 34px rgba(0,0,0,0.10)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(0,0,0,0.05)";
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  minWidth: 0
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedFileKeys.includes(getFileKey(file))}
                  onChange={(e) => {
                    const fileKey = getFileKey(file);
                    setSelectedFileKeys((current) =>
                      e.target.checked
                        ? [...new Set([...current, fileKey])]
                        : current.filter((item) => item !== fileKey)
                    );
                  }}
                  style={{ marginTop: 7 }}
                />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap"
                  }}
                >
                  <span style={{ fontSize: 24 }}>{getFileIcon(file.name)}</span>

                  <strong style={{ wordBreak: "break-word" }}>
                    {file.name || "Dosya"}
                  </strong>

                  <span style={getCategoryBadgeStyle(file.category || "Genel")}>
                    {file.category || "Genel"}
                  </span>
                </div>

                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>
                  {file.firmaAdi || "-"} • {file.projeAdi || "-"}
                </div>

                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 5 }}>
                  {file.teknoparkAdi || "-"} • STB: {file.projeStbKodu || "-"}
                </div>

                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 5 }}>
                  📦 {formatFileSize(file.size)} •{" "}
                  {file.uploadedAt
                    ? new Date(file.uploadedAt).toLocaleString("tr-TR")
                    : "-"}
                </div>
              </div>
              </label>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={() => setPreviewFile(file)}
                  style={secondaryButtonStyle}
                >
                  Görüntüle
                </button>

                <button
                  onClick={() => {
                    const project = projects.find((p) => p.id === file.projectId);

                    if (project) {
                      setSelectedProject(project);
                      setEditProject(project);
                    }
                  }}
                  style={secondaryButtonStyle}
                >
                  Projeyi Aç
                </button>

                <a
                  href={file.url}
                  target="_blank"
                  rel="noreferrer"
                  download
                  style={secondaryButtonStyle}
                >
                  İndir
                </a>

                {canEdit && (
                  <button
                    onClick={() => deleteProjectFileFromFilesView(file)}
                    style={deleteButtonStyle}
                  >
                    Sil
                  </button>
                )}
              </div>
            </div>
          ))}

          {filteredFiles.length === 0 && (
            <div
              style={{
                padding: 24,
                borderRadius: 14,
                background: "#f9fafb",
                border: "1px dashed #d1d5db",
                color: "#6b7280",
                textAlign: "center",
                fontWeight: 600
              }}
            >
              Eşleşen dosya bulunamadı.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid #dbe2ea",
  outline: "none"
};

const summaryCardStyle = {
  background: "#fff",
  padding: 20,
  borderRadius: 18,
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)"
};

const summaryLabelStyle = {
  fontSize: 13,
  color: "#6b7280",
  fontWeight: 700
};

const summaryValueStyle = {
  fontSize: 28,
  fontWeight: 900,
  color: "#124254",
  marginTop: 8
};

const categoryButtonStyle = {
  padding: "9px 13px",
  borderRadius: 999,
  border: "1px solid #e5e7eb",
  fontWeight: 700,
  cursor: "pointer"
};

const fileCardStyle = {
  padding: "18px 20px",
  borderRadius: 18,
  border: "1px solid #e5e7eb",
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: 16,
  alignItems: "center",
  background: "#fff",
  boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
  transition: "all 0.18s ease"
};

const getCategoryBadgeStyle = (category) => {
  const styles = {
    "Genel": {
      background: "#eef2ff",
      color: "#4338ca"
    },
    "Faaliyet Raporu": {
      background: "#dcfce7",
      color: "#166534"
    },
    "YMM": {
      background: "#fee2e2",
      color: "#b91c1c"
    },
    "Proje Belgesi": {
      background: "#dbeafe",
      color: "#1d4ed8"
    },
    "Proje Bitirme Belgesi": {
      background: "#ffedd5",
      color: "#c2410c"
    },
    "Yazılım Tescil": {
      background: "#f3e8ff",
      color: "#7e22ce"
    },
    "Sözleşme": {
      background: "#fef3c7",
      color: "#92400e"
    },
    "Fatura": {
      background: "#e0f2fe",
      color: "#0369a1"
    },
    "Sunum": {
      background: "#fce7f3",
      color: "#be185d"
    },
    "Diğer": {
      background: "#f3f4f6",
      color: "#374151"
    }
  };

  return {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    ...(styles[category] || styles["Genel"])
  };
};

const getCategoryButtonStyle = (category, isActive) => {
  const badgeStyle = getCategoryBadgeStyle(category);

  return {
    ...categoryButtonStyle,
    background: isActive ? badgeStyle.background : "#f8fafc",
    color: isActive ? badgeStyle.color : "#111827",
    border: isActive
      ? `1px solid ${badgeStyle.color}`
      : "1px solid #e5e7eb"
  };
};

const secondaryButtonStyle = {
  padding: "9px 13px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#111827",
  fontWeight: 700,
  cursor: "pointer",
  textDecoration: "none"
};

const deleteButtonStyle = {
  padding: "9px 13px",
  borderRadius: 10,
  border: "none",
  background: "#ef4444",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer"
};
